// components/Moldura.tsx - O shell da WiniEduca dentro da Dinâmica (entrega 1.6; D83, D85, D173).
//
// O mesmo AppShell do hub: as cinco seções (Criar ativo, porque a Dinâmica é o "Criar"), "Letra" com a
// escolha salva no perfil do professor (D84), o botão do perfil, o caminho "Início › Criar dinâmica" no
// computador e "‹ Início" no celular. As outras seções ficam no hub, pelo endereço configurado (D46).

import { useEffect, useLayoutEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AppShell, Footer, SHELL_TEXT, defaultNav, useReadingPrefs, type LinkComponent, type ReadingPrefs } from '@winieduca/ui';
import { hubUrl } from '../config/hub';
import { useAuth } from '../contexts/AuthContext';
import { pagina } from '../content/textos';
import { acompanharPerfil, iniciais, salvarLeitura, type PerfilDaDinamica } from '../services/perfil';

export const NAV = defaultNav({
  inicio: hubUrl('/inicio'),
  criar: '/',
  videos: hubUrl('/videos'),
  comunidade: hubUrl('/comunidade'),
  perfil: hubUrl('/perfil'),
});

/**
 * Link do shell: rota da própria Dinâmica pelo React Router; o resto é o hub (outro endereço). O pacote traz
 * os próprios tipos do React (outra cópia), por isso os atributos passam como estão, sem conferir de novo.
 */
export const LinkDaDinamica: LinkComponent = (props) => {
  const { href, children, ...rest } = props as { href: string; children?: ReactNode } & Record<string, unknown>;
  return href.startsWith('/') ? (
    <Link to={href} {...rest}>
      {children}
    </Link>
  ) : (
    <a href={href} {...rest}>
      {children}
    </a>
  );
};

/** A base visual nova vale com a classe `we-ui` no <html> (entrega 1.1). */
function useBaseVisualNova() {
  useLayoutEffect(() => {
    document.documentElement.classList.add('we-ui');
    return () => document.documentElement.classList.remove('we-ui');
  }, []);
}

/** Letra e contraste salvos no perfil, os mesmos do hub (D84); a escolha feita aqui volta para o perfil. */
function useLeituraDoPerfil(perfil: PerfilDaDinamica | null | undefined, uid: string | null) {
  return useReadingPrefs({
    initial: perfil?.readingPrefs ?? null,
    onChange: uid ? (p: ReadingPrefs) => void salvarLeitura(uid, p).catch(() => undefined) : undefined,
  });
}

export function Moldura({ children, rodape }: { children: ReactNode; rodape?: ReactNode }) {
  useBaseVisualNova();
  const { currentUser } = useAuth();
  const uid = currentUser?.uid ?? null;
  const [perfil, setPerfil] = useState<PerfilDaDinamica | null | undefined>(undefined);

  useEffect(() => {
    if (!uid) {
      setPerfil(null);
      return;
    }
    return acompanharPerfil(uid, setPerfil);
  }, [uid]);

  const { prefs, update } = useLeituraDoPerfil(perfil, uid);

  return (
    <AppShell
      nav={NAV}
      currentKey="criar"
      homeHref={hubUrl('/inicio')}
      linkAs={LinkDaDinamica}
      user={uid ? { initials: iniciais(perfil?.nome), profileHref: hubUrl('/perfil') } : undefined}
      reading={{ prefs, onChange: update }}
      trail={[{ label: SHELL_TEXT.nav.inicio, href: hubUrl('/inicio') }, { label: pagina.caminho }]}
      footer={<Footer>{rodape}</Footer>}
    >
      {children}
    </AppShell>
  );
}
